import { ConfigSection } from './GeneralConfig'
import { useMemo, useState, type FC } from 'react'
import { getUseConfigStore } from '../../stores/useConfigStore'
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { CircleHelp, ThumbsDown, ThumbsUp } from 'lucide-react'
import { Composer, Message } from '@botpress/webchat'
import { FileCardDisplay } from '../elements/FileCardDisplay'
import { cn } from '~/utils'
import { WebchatComponentPreview } from '../elements/WebchatComponentPreview'
import { useFeature } from '~/hooks'
import { SoundSettingPreview } from '../elements/SoundSettingPreview'
import { ChatInterfaceForm } from '../elements/ChatInterfaceForm'

type Props = {
  workspaceId: string
  botId: string
}

export const SettingsSection: FC<Props> = ({ workspaceId, botId }) => {
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null)

  const injectToggleEmbedFeatureEnabled = useFeature('webchat-inject-toggle-embed')
  const webchatSoundEffects = useFeature('webchat-sound-effects')

  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])

  const feedbackEnabled = useConfigStore((state) => state.feedbackEnabled)
  const embeddedChatId = useConfigStore((state) => state.embeddedChatId)
  const toggleChatId = useConfigStore((state) => state.toggleChatId)
  const allowFileUpload = useConfigStore((state) => state.allowFileUpload)
  const storageLocation = useConfigStore((state) => state.storageLocation)
  const composerPlaceholder = useConfigStore((state) => state.composerPlaceholder)
  const botAvatar = useConfigStore((state) => state.botAvatar)
  const fabImage = useConfigStore((state) => state.fabImage)
  const soundEnabled = useConfigStore((state) => state.soundEnabled)

  const setFeedbackEnabled = useConfigStore((state) => state.setFeedbackEnabled)
  const setAllowFileUpload = useConfigStore((state) => state.setAllowFileUpload)
  const setStorageLocation = useConfigStore((state) => state.setStorageLocation)

  const setSoundEnabled = useConfigStore((state) => state.setSoundEnabled)
  const setEmbeddedChatId = useConfigStore((state) => state.setEmbeddedChatId)
  const setToggleChatId = useConfigStore((state) => state.setToggleChatId)
  const setFabImage = useConfigStore((state) => state.setFabImage)

  return (
    <ConfigSection title="Settings" description="Configure the webchat's behavior.">
      <div className="flex flex-col gap-6">
        {injectToggleEmbedFeatureEnabled && (
          <ChatInterfaceForm
            embeddedChatId={embeddedChatId}
            toggleChatId={toggleChatId}
            setEmbeddedChatId={setEmbeddedChatId}
            setToggleChatId={setToggleChatId}
            workspaceId={workspaceId}
            botId={botId}
            fabImage={fabImage}
            setFabImage={setFabImage}
            botAvatar={botAvatar}
          />
        )}

        <div className="flex w-full flex-col space-y-2 ">
          <div className="flex items-center justify-between ">
            <div className="flex gap-1">
              <Label className="text-muted-foreground">Message feedback</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleHelp className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Let users rate bot messages with thumbs up/down. View feedback analytics in the Analytics tab.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Switch checked={feedbackEnabled} onCheckedChange={(value) => setFeedbackEnabled(value)} />
          </div>
          <WebchatComponentPreview className="flex h-24 items-center justify-center rounded-lg border bg-neutral-50 px-8">
            <Message
              direction="incoming"
              sender={{ name: 'Bot' }}
              id="test"
              timestamp={new Date()}
              block={{
                type: 'bubble',
                block: {
                  type: 'text',
                  text: 'This is a message',
                },
              }}
            >
              <div
                className={cn('bpMessageBlocksBubbleFeedbackContainer !absolute !-bottom-4 !left-36', {
                  '!opacity-100': feedbackEnabled,
                })}
              >
                <ThumbsUp
                  className="bpMessageBlocksBubbleFeedbackIcon"
                  data-selected={feedback === 'positive'}
                  onClick={() => setFeedback('positive')}
                />
                <ThumbsDown
                  className="bpMessageBlocksBubbleFeedbackIcon"
                  data-selected={feedback === 'negative'}
                  onClick={() => setFeedback('negative')}
                />
              </div>
            </Message>
          </WebchatComponentPreview>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex w-full items-center justify-between">
            <Label className="text-muted-foreground">Allow file upload</Label>
            <Switch checked={allowFileUpload} onCheckedChange={(value) => setAllowFileUpload(value)} />
          </div>
          <WebchatComponentPreview className="pointer-events-none flex h-40 items-center justify-center rounded-lg border bg-neutral-50 px-4 pt-3">
            <Composer
              className="w-full"
              connected={true}
              allowFileUpload={allowFileUpload}
              composerPlaceholder={composerPlaceholder}
              storageKey="composer-display"
            >
              <FileCardDisplay allowFileUpload={allowFileUpload} />
            </Composer>
          </WebchatComponentPreview>
        </div>

        {webchatSoundEffects && (
          <div className="flex flex-col gap-2">
            <div className="flex w-full items-center justify-between">
              <div className="flex gap-1">
                <Label className="text-muted-foreground">Enable sound</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <CircleHelp className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Enable sound notifications for new messages. Requires Webchat v3.3+</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <Switch checked={soundEnabled} onCheckedChange={setSoundEnabled} />
            </div>
            <SoundSettingPreview soundEnabled={soundEnabled ?? false} botId={botId} workspaceId={workspaceId} />
          </div>
        )}

        <div className="flex w-fit flex-col gap-2">
          <div className="flex items-center gap-1">
            <Label className="text-muted-foreground">Persist conversations</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <CircleHelp className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Persiting chat history will let your users continue conversations where they left off. Even if they
                    close their browser.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select
            value={storageLocation ? storageLocation : 'localStorage'}
            onValueChange={(value) => setStorageLocation(value as 'localStorage' | 'sessionStorage')}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select storage type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="localStorage">Across browser tabs</SelectItem>
              <SelectItem value="sessionStorage">One tab only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </ConfigSection>
  )
}
